/**
 * Punching, Work from home and "Doesn't need to punch" (migration 167;
 * docs/superpowers/specs/2026-10-30-attendance-today-calendar.md §3).
 *
 * The server decides whether a punch was required (database.PunchRequired)
 * and tells Today which days of the last fortnight needed one
 * (GET /attendance/today `punching.required_days`). These helpers only
 * read that answer — they never re-derive the rule.
 */

import type { PunchingMode, TodayPunching, WFHMode } from "@/types/attendance";

export interface ModeOption<T extends string> {
  value: T;
  label: string;
  /** One plain-English line under the option. */
  help: string;
}

export const PUNCHING_OPTIONS: readonly ModeOption<PunchingMode>[] = [
  {
    value: "off",
    label: "Off",
    help: "No Punch In button. A working day without leave counts as present.",
  },
  {
    value: "optional",
    label: "Optional",
    help: "People can punch, but a day without a punch still counts as present. No reminders, no loss of pay.",
  },
  {
    value: "required",
    label: "Required",
    help: "A working day with no punch and no leave shows “?” until it is fixed with I Was Working or leave.",
  },
];

export const WFH_OPTIONS: readonly ModeOption<WFHMode>[] = [
  {
    value: "off",
    label: "Off",
    help: "No Home choice anywhere. Work From Home is not used.",
  },
  {
    value: "allowed",
    label: "Allowed",
    help: "Punching in from Home (or tapping Working From Home Today) marks the day Work From Home straight away.",
  },
  {
    value: "approval",
    label: "Needs Manager Approval",
    help: "Same, but the manager gets a request in the Inbox. If they decline, the day shows ? until it is fixed.",
  },
];

/** "Off" / "Optional" / "Required" for a mode (Required when unknown). */
export function punchingLabel(mode: PunchingMode | undefined): string {
  return PUNCHING_OPTIONS.find((o) => o.value === mode)?.label ?? "Required";
}

/** Today shows the Punch In button unless the company's Punching is Off.
 *  No `punching` block (older server) keeps the button. */
export function showPunchButton(p: TodayPunching | undefined): boolean {
  return p?.mode !== "off";
}

/** Work From Home is offered unless the company switched it off. Unknown
 *  (no value on /me) keeps it. */
export function wfhOffered(wfhMode: string | undefined): boolean {
  return wfhMode !== "off";
}

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
}

/**
 * Was a punch required of the person on `date`? Inside the fortnight the
 * server answered for (the 14 days before today) it is that answer; for an
 * older date it falls back to today's settings (Required and not exempt).
 * No `punching` block (older server) → true, i.e. the page as before.
 */
export function punchNeededOn(p: TodayPunching | undefined, date: string, today: string): boolean {
  if (!p) return true;
  if (date >= addDays(today, -14) && date < today) {
    return p.required_days.includes(date);
  }
  return p.mode === "required" && !p.exempt;
}

/** The employee-profile "Punching" choice. */
export type PunchingChoice = "company" | "not_required";

/** The Work details select's options, naming the company setting. */
export function punchingChoices(mode: PunchingMode | undefined): readonly { value: PunchingChoice; label: string }[] {
  return [
    { value: "company", label: `Company setting (${punchingLabel(mode)})` },
    { value: "not_required", label: "Doesn't need to punch" },
  ];
}
